import { useEffect, useMemo, useRef, useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-hot-toast';
import {
  ArrowLeft,
  File,
  Inbox,
  Loader2,
  Mail,
  Paperclip,
  Search,
  Send,
  Trash2,
} from 'lucide-react';
import { useActiveClinic } from '@/lib/activeClinic';
import { useAuth } from '@/lib/auth';
import { useClinic } from '@/hooks/useClinicSettings';
import { useIsDesktopNav } from '@/hooks/useNavBreakpoint';
import {
  useArchivePatientEmailThread,
  useLinkPatientEmailThread,
  useMarkPatientEmailThreadRead,
  useMarkPatientEmailThreadUnread,
  useOwnEmailSignature,
  useSendPatientEmail,
  useUnmatchedEmailLatestMessages,
  useUnmatchedEmailThreadReads,
  useUnmatchedEmailThreads,
  type PatientEmailThread,
} from '@/hooks/usePatientEmailThreads';
import { EmailThreadTranscript } from '@/components/patient/PatientEmailThreads';
import {
  Banner,
  Button,
  Card,
  EmptyState,
  Input,
  SegmentedControl,
  Skeleton,
  SkeletonText,
  Textarea,
} from '@/components/ui';
import { Avatar } from '@/components/ui/Avatar';
import { containsCpr } from '@/lib/clinicEmail/catalog';
import {
  EMAIL_ATTACHMENT_MAX_COUNT,
  fileToBase64,
  validateEmailComposeAttachment,
} from '@/lib/patientEmail';
import {
  filterUnmatchedEmailThreads,
  isUnmatchedEmailThreadUnread,
  replySubjectForThread,
  unmatchedEmailReadByThreadId,
  type UnmatchedEmailListFilter,
  type UnmatchedEmailListItem,
  type UnmatchedEmailThreadRead,
} from '@/lib/unmatchedEmailInbox';
import { supabase } from '@/lib/supabase';
import { TYPE, TYPE_TABULAR } from '@/lib/typography';
import { cn } from '@/utils/cn';
import { formatInboxListWhen, inboxLocale } from '@/utils/messageInboxFormat';

type PatientOption = { id: string; label: string };

export function UnmatchedEmailInbox({
  highlightThreadId,
  onMobileConversationChange,
}: {
  highlightThreadId?: string | null;
  onMobileConversationChange?: (open: boolean) => void;
}) {
  const { t, i18n } = useTranslation();
  const { activeClinicId } = useActiveClinic();
  const { user, isPlatformAdmin, isClinicAdmin, hasPermission } = useAuth();
  const isDesktop = useIsDesktopNav();
  const locale = inboxLocale(i18n.language);
  const threadsQuery = useUnmatchedEmailThreads(activeClinicId);
  const readsQuery = useUnmatchedEmailThreadReads(activeClinicId, user?.id);
  const latestQuery = useUnmatchedEmailLatestMessages(activeClinicId);

  const [filter, setFilter] = useState<UnmatchedEmailListFilter>('unread');
  const [search, setSearch] = useState('');
  const [selectedId, setSelectedId] = useState<string | null>(highlightThreadId ?? null);

  useEffect(() => {
    if (highlightThreadId) setSelectedId(highlightThreadId);
  }, [highlightThreadId]);

  const threads = useMemo(() => threadsQuery.data ?? [], [threadsQuery.data]);
  const reads = useMemo(() => readsQuery.data ?? [], [readsQuery.data]);
  const latestByThread = latestQuery.data;
  const readByThread = useMemo(() => unmatchedEmailReadByThreadId(reads), [reads]);

  const listItems = useMemo<UnmatchedEmailListItem[]>(
    () =>
      threads.map((thread) => {
        const latest = latestByThread?.get(thread.id);
        return {
          id: thread.id,
          last_inbound_at: thread.last_inbound_at,
          last_message_at: thread.last_message_at,
          subject: thread.subject ?? latest?.subject ?? null,
          fromAddress: latest?.from_address ?? null,
          preview: latest?.text_body ?? null,
        };
      }),
    [latestByThread, threads],
  );

  const visible = useMemo(
    () => filterUnmatchedEmailThreads(listItems, reads, { tab: filter, search }),
    [filter, listItems, reads, search],
  );

  useEffect(() => {
    if (selectedId && visible.some((item) => item.id === selectedId)) return;
    if (selectedId && threads.some((item) => item.id === selectedId) && isDesktop) return;
    if (isDesktop && visible[0]) {
      setSelectedId(visible[0].id);
      return;
    }
    if (selectedId && !threads.some((item) => item.id === selectedId)) {
      setSelectedId(null);
    }
  }, [isDesktop, selectedId, threads, visible]);

  const selected = threads.find((thread) => thread.id === selectedId) ?? null;
  const selectedItem = listItems.find((item) => item.id === selectedId) ?? null;
  const mobileConversation = !isDesktop && selected != null;

  useEffect(() => {
    onMobileConversationChange?.(mobileConversation);
  }, [mobileConversation, onMobileConversationChange]);

  const canManage = Boolean(
    activeClinicId &&
      (isPlatformAdmin ||
        isClinicAdmin(activeClinicId) ||
        hasPermission(activeClinicId, 'communication', 'view') ||
        hasPermission(activeClinicId, 'patient_email', 'view')),
  );

  if (!activeClinicId) {
    return (
      <Banner tone="error">{t('messages.inbox.no_clinic', 'You have no active clinic.')}</Banner>
    );
  }

  return (
    <div
      className={cn(
        'grid min-h-0 min-w-0 flex-1 gap-s5',
        mobileConversation ? 'grid-cols-1' : 'grid-cols-1 nav:grid-cols-[minmax(0,380px)_minmax(0,1fr)]',
      )}
    >
      <Card
        className={cn(
          'flex min-h-0 min-w-0 flex-col overflow-hidden p-s4',
          mobileConversation && 'hidden',
        )}
      >
        <div className="flex min-w-0 flex-wrap items-center gap-s3">
          <SegmentedControl
            ariaLabel={t('messages.inbox.view', 'Visning')}
            value={filter}
            onChange={setFilter}
            options={[
              { value: 'unread', label: t('messages.inbox.unread', 'Ulæste') },
              { value: 'all', label: t('messages.inbox.all', 'Alle') },
            ]}
          />
        </div>
        <div className="mt-s3 min-w-0">
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder={t('communicationInbox.email_search', 'Søg i e-mails…')}
            leadingIcon={<Search className="h-4 w-4" />}
            aria-label={t('communicationInbox.email_search', 'Søg i e-mails…')}
          />
        </div>
        <div className="mt-s3 min-h-0 flex-1 overflow-y-auto">
          {threadsQuery.error ? (
            <Banner
              tone="error"
              title={t('communicationInbox.email_load_error', 'Kunne ikke hente e-mails')}
              action={
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  onClick={() => void threadsQuery.refetch()}
                >
                  {t('app.retry')}
                </Button>
              }
            />
          ) : threadsQuery.isLoading ? (
            <ThreadListSkeleton />
          ) : visible.length === 0 ? (
            <EmptyState
              icon={<Inbox className="h-7 w-7" />}
              title={
                search
                  ? t('communicationInbox.email_empty_search', 'Ingen matchende e-mails')
                  : filter === 'unread'
                    ? t('communicationInbox.email_empty_unread', 'Ingen ulæste e-mails')
                    : t('settings.email.inbox_empty', 'Ingen uidentificerede e-mails.')
              }
              description={
                search
                  ? undefined
                  : t(
                      'communicationInbox.email_empty_body',
                      'Når en e-mail ikke kan knyttes til én patient, vises den her.',
                    )
              }
            />
          ) : (
            <ul className="flex flex-col gap-s1">
              {visible.map((item) => (
                <EmailThreadRow
                  key={item.id}
                  item={item}
                  locale={locale}
                  unread={isUnmatchedEmailThreadUnread(item, readByThread.get(item.id))}
                  selected={item.id === selectedId}
                  onSelect={() => setSelectedId(item.id)}
                />
              ))}
            </ul>
          )}
        </div>
      </Card>

      <Card
        className={cn(
          'flex min-h-0 min-w-0 flex-col overflow-hidden p-0',
          !mobileConversation && 'hidden nav:flex',
          mobileConversation && 'flex',
        )}
      >
        <EmailConversationPanel
          clinicId={activeClinicId}
          thread={selected}
          listItem={selectedItem}
          read={selected ? readByThread.get(selected.id) : undefined}
          canManage={canManage}
          showBack={!isDesktop}
          onBack={() => setSelectedId(null)}
          onGone={() => setSelectedId(null)}
        />
      </Card>
    </div>
  );
}

function EmailThreadRow({
  item,
  locale,
  unread,
  selected,
  onSelect,
}: {
  item: UnmatchedEmailListItem;
  locale: ReturnType<typeof inboxLocale>;
  unread: boolean;
  selected: boolean;
  onSelect: () => void;
}) {
  const { t } = useTranslation();
  const name = item.fromAddress || t('email.no_subject', 'Uden emne');
  const when = item.last_inbound_at ?? item.last_message_at ?? null;

  return (
    <li>
      <button
        type="button"
        onClick={onSelect}
        aria-current={selected ? 'true' : undefined}
        className={cn(
          'flex w-full min-w-0 items-start gap-s3 rounded-pop p-s3 text-left',
          selected
            ? 'border border-ds-teal-line bg-ds-teal-tint'
            : 'border border-transparent hover:bg-ds-neutral-tint',
        )}
      >
        <Avatar name={name} size={40} />
        <span className="min-w-0 flex-1">
          <span className="flex items-start gap-s2">
            <span className={cn(TYPE.control, 'min-w-0 flex-1 truncate font-semibold text-ds-text')}>
              {item.fromAddress || t('communicationInbox.unknown_sender', 'Ukendt afsender')}
            </span>
            <span className={cn(TYPE.meta, TYPE_TABULAR, 'shrink-0 font-medium text-ds-muted')}>
              {when ? formatInboxListWhen(when, locale) : ''}
            </span>
          </span>
          <span className={cn(TYPE.small, 'mt-s1 block truncate font-medium text-ds-text')}>
            {item.subject || t('email.no_subject', 'Uden emne')}
          </span>
          {item.preview ? (
            <span className={cn(TYPE.small, 'mt-s1 block truncate text-ds-muted')}>
              {item.preview}
            </span>
          ) : null}
        </span>
        {unread ? (
          <span
            className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-ds-coral-badge"
            aria-label={t('messages.inbox.unread_indicator', 'Ulæst')}
          />
        ) : null}
      </button>
    </li>
  );
}

function EmailConversationPanel({
  clinicId,
  thread,
  listItem,
  read,
  canManage,
  showBack,
  onBack,
  onGone,
}: {
  clinicId: string;
  thread: PatientEmailThread | null;
  listItem: UnmatchedEmailListItem | null;
  read: UnmatchedEmailThreadRead | undefined;
  canManage: boolean;
  showBack: boolean;
  onBack: () => void;
  onGone: () => void;
}) {
  const { t } = useTranslation();
  const unread = thread ? isUnmatchedEmailThreadUnread(thread, read) : false;
  const markRead = useMarkPatientEmailThreadRead(clinicId);
  const markUnread = useMarkPatientEmailThreadUnread(clinicId);
  const archive = useArchivePatientEmailThread(clinicId);
  const clinic = useClinic(clinicId);

  if (!thread) {
    return (
      <EmptyState
        icon={<Mail className="h-7 w-7" />}
        title={t('communicationInbox.email_select_title', 'Vælg en e-mail')}
        description={t(
          'communicationInbox.email_select_body',
          'Åbn en e-mail i listen for at læse, svare eller knytte den til en patient.',
        )}
        className="h-full justify-center"
      />
    );
  }

  const onArchive = async () => {
    try {
      await archive.mutateAsync(thread.id);
      toast.success(t('settings.email.inbox_archived', 'E-mailen er arkiveret.'));
      onGone();
    } catch {
      toast.error(t('settings.email.inbox_archive_failed', 'Kunne ikke arkivere e-mailen.'));
    }
  };

  const onToggleRead = async () => {
    try {
      if (unread) {
        await markRead.mutateAsync(thread.id);
        toast.success(t('communicationInbox.marked_read', 'Markeret som læst'));
      } else {
        await markUnread.mutateAsync(thread.id);
        toast.success(t('communicationInbox.marked_unread', 'Markeret som ulæst'));
      }
    } catch {
      toast.error(t('communicationInbox.read_failed', 'Kunne ikke ændre læst-status.'));
    }
  };

  return (
    <section className="flex min-h-0 min-w-0 flex-1 flex-col" aria-labelledby="email-conversation-title">
      <header className="flex shrink-0 items-start gap-s3 border-b border-ds-line px-s4 py-s4 nav:px-s5">
        {showBack ? (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={onBack}
            aria-label={t('messages.inbox.back', 'Tilbage til listen')}
          >
            <ArrowLeft />
          </Button>
        ) : null}
        <Avatar
          name={listItem?.fromAddress || t('communicationInbox.unknown_sender', 'Ukendt afsender')}
          size={40}
        />
        <div className="min-w-0 flex-1">
          <h2 id="email-conversation-title" className={cn(TYPE.section, 'truncate text-ds-text')}>
            {listItem?.fromAddress || t('communicationInbox.unknown_sender', 'Ukendt afsender')}
          </h2>
          <p className={cn(TYPE.small, 'truncate text-ds-muted')}>
            {thread.subject || t('email.no_subject', 'Uden emne')}
          </p>
        </div>
        {canManage ? (
          <div className="flex shrink-0 flex-wrap justify-end gap-s2">
            <Button
              type="button"
              variant="tertiary"
              size="sm"
              disabled={markRead.isPending || markUnread.isPending}
              onClick={() => void onToggleRead()}
            >
              {unread
                ? t('communicationInbox.mark_read', 'Markér som læst')
                : t('communicationInbox.mark_unread', 'Markér som ulæst')}
            </Button>
            <Button
              type="button"
              variant="secondary"
              size="sm"
              disabled={archive.isPending}
              onClick={() => void onArchive()}
            >
              {t('settings.email.inbox_archive', 'Arkivér')}
            </Button>
          </div>
        ) : null}
      </header>

      <div className="min-h-0 flex-1 overflow-y-auto px-s4 py-s5 nav:px-s5">
        <EmailThreadTranscript thread={thread} clinicName={clinic.data?.name} />
        {canManage ? <LinkPatientCard clinicId={clinicId} threadId={thread.id} onLinked={onGone} /> : null}
      </div>

      {canManage ? (
        <UnmatchedEmailReplyForm
          clinicId={clinicId}
          thread={thread}
          clinicEmailReady={Boolean((clinic.data?.email_local_part ?? '').trim())}
          clinicSignature={clinic.data?.email_signature_text ?? ''}
        />
      ) : null}
    </section>
  );
}

function LinkPatientCard({
  clinicId,
  threadId,
  onLinked,
}: {
  clinicId: string;
  threadId: string;
  onLinked: () => void;
}) {
  const { t } = useTranslation();
  const link = useLinkPatientEmailThread(clinicId);
  const [query, setQuery] = useState('');
  const [options, setOptions] = useState<PatientOption[]>([]);
  const [selected, setSelected] = useState<PatientOption | null>(null);

  const searchPatients = async (value: string) => {
    setQuery(value);
    const needle = value.replace(/[%_,]/g, '').trim();
    if (!needle) {
      setOptions([]);
      return;
    }
    const { data, error } = await supabase
      .from('patients')
      .select('id, first_name, last_name')
      .eq('clinic_id', clinicId)
      .eq('status', 'active')
      .or(`first_name.ilike.%${needle}%,last_name.ilike.%${needle}%,email.ilike.%${needle}%`)
      .order('last_name', { ascending: true })
      .limit(8);
    if (error) return;
    setOptions(
      (data ?? []).map((row) => ({
        id: row.id,
        label: `${row.first_name ?? ''} ${row.last_name ?? ''}`.trim() || row.id,
      })),
    );
  };

  const onLink = async () => {
    if (!selected) return;
    try {
      await link.mutateAsync({ threadId, patientId: selected.id });
      toast.success(t('settings.email.inbox_linked', 'E-mailen er knyttet til patienten.'));
      onLinked();
    } catch {
      toast.error(t('settings.email.inbox_link_failed', 'Kunne ikke knytte e-mailen.'));
    }
  };

  return (
    <div className="mt-s5 rounded-xl bg-ds-sunk p-s3">
      <div className={cn(TYPE.label, 'mb-s2 text-ds-text')}>
        {t('settings.email.inbox_link_patient', 'Knyt til patient')}
      </div>
      <div className="flex min-w-0 flex-col gap-s2 nav:flex-row nav:items-end">
        <div className="min-w-0 flex-1">
          <Input
            value={selected ? selected.label : query}
            onChange={(e) => {
              setSelected(null);
              void searchPatients(e.target.value);
            }}
            autoComplete="off"
            aria-label={t('settings.email.inbox_link_patient', 'Knyt til patient')}
          />
          {options.length > 0 && !selected ? (
            <ul className="mt-s1 rounded-xl border border-ds-line bg-ds-card">
              {options.map((option) => (
                <li key={option.id}>
                  <button
                    type="button"
                    className="block w-full px-s3 py-s2 text-left text-sm hover:bg-ds-neutral-tint"
                    onClick={() => {
                      setSelected(option);
                      setQuery(option.label);
                      setOptions([]);
                    }}
                  >
                    {option.label}
                  </button>
                </li>
              ))}
            </ul>
          ) : null}
        </div>
        <Button type="button" size="sm" disabled={!selected || link.isPending} onClick={() => void onLink()}>
          {t('settings.email.inbox_link', 'Knyt')}
        </Button>
      </div>
    </div>
  );
}

function UnmatchedEmailReplyForm({
  clinicId,
  thread,
  clinicEmailReady,
  clinicSignature,
}: {
  clinicId: string;
  thread: PatientEmailThread;
  clinicEmailReady: boolean;
  clinicSignature: string;
}) {
  const { t } = useTranslation();
  const { user } = useAuth();
  const sendEmail = useSendPatientEmail();
  const markRead = useMarkPatientEmailThreadRead(clinicId);
  const { data: personalSignature = '' } = useOwnEmailSignature(clinicId, user?.id);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [subject, setSubject] = useState(() => replySubjectForThread(thread.subject));
  const [body, setBody] = useState('');
  const [files, setFiles] = useState<File[]>([]);
  const [sending, setSending] = useState(false);
  const signaturePreview = [personalSignature, clinicSignature].filter((s) => s.trim()).join('\n\n');

  useEffect(() => {
    setSubject(replySubjectForThread(thread.subject));
    setBody('');
    setFiles([]);
  }, [thread.id, thread.subject]);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const next = Array.from(e.target.files || []);
    const valid: File[] = [];
    for (const file of next) {
      const validationError = validateEmailComposeAttachment(file);
      if (validationError === 'size') {
        toast.error(t('email.attachment_too_large', `Filen ${file.name} er for stor (max 10 MB)`));
      } else if (validationError === 'type') {
        toast.error(t('email.attachment_invalid_type', `Filtypen for ${file.name} er ikke tilladt`));
      } else {
        valid.push(file);
      }
    }
    setFiles((prev) => [...prev, ...valid].slice(0, EMAIL_ATTACHMENT_MAX_COUNT));
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    if (!subject.trim() || !body.trim()) {
      toast.error(t('email.compose_required', 'Emne og brødtekst er påkrævet.'));
      return;
    }
    if (containsCpr(subject) || containsCpr(body)) {
      toast.error(t('email.cpr_blocked', 'CPR må ikke indgå i e-mails.'));
      return;
    }
    setSending(true);
    try {
      const attachments = await Promise.all(
        files.map(async (file) => ({
          filename: file.name,
          content: await fileToBase64(file),
          contentType: file.type || undefined,
        })),
      );
      await sendEmail.mutateAsync({
        clinic_id: clinicId,
        subject: subject.trim(),
        body,
        thread_id: thread.id,
        attachments: attachments.length > 0 ? attachments : undefined,
      });
      await markRead.mutateAsync(thread.id);
      toast.success(t('email.send_success', 'E-mail sendt.'));
      setBody('');
      setFiles([]);
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : t('email.send_failed', 'E-mail kunne ikke sendes.');
      toast.error(message);
    } finally {
      setSending(false);
    }
  };

  if (!clinicEmailReady) {
    return (
      <p className={cn(TYPE.small, 'shrink-0 border-t border-ds-line px-s4 py-s4 text-ds-amber-ink nav:px-s5')}>
        {t(
          'patients.communication.email_needs_clinic_address',
          'Klinikken mangler en @clinio.app-adresse under Indstillinger → E-mail.',
        )}
      </p>
    );
  }

  return (
    <form
      onSubmit={(e) => void onSubmit(e)}
      className="shrink-0 border-t border-ds-line px-s4 py-s4 nav:px-s5 nav:pb-s5"
    >
      <Input
        name="unmatched-email-subject"
        label={t('email.subject', 'Emne')}
        value={subject}
        onChange={(e) => setSubject(e.target.value)}
        maxLength={300}
        autoComplete="off"
      />
      <div className="mt-s3">
        <Textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          placeholder={t('communicationInbox.reply_placeholder', 'Skriv et svar…')}
          rows={4}
          maxLength={20000}
          disabled={sending}
        />
      </div>
      {signaturePreview ? (
        <p className={cn(TYPE.small, 'mt-s2 whitespace-pre-wrap text-ds-muted')}>
          {t('email.signature_preview', 'Signatur (personlig + klinik)')}
          {':\n'}
          {signaturePreview}
        </p>
      ) : null}
      <input
        ref={fileInputRef}
        type="file"
        onChange={handleFileSelect}
        multiple
        accept=".pdf,.jpg,.jpeg,.png,.gif,.webp,.doc,.docx,.xls,.xlsx,.txt"
        className="hidden"
      />
      <div className="mt-s3 flex flex-wrap items-center justify-between gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={() => fileInputRef.current?.click()}
          disabled={sending || files.length >= EMAIL_ATTACHMENT_MAX_COUNT}
        >
          <Paperclip />
          {t('email.attach_file', 'Vedhæft fil')}
        </Button>
        <Button type="submit" disabled={sending || !subject.trim() || !body.trim()}>
          {sending ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              {t('email.sending', 'Sender…')}
            </>
          ) : (
            <>
              <Send className="mr-2 h-4 w-4" />
              {t('patients.communication.reply_email', 'Svar')}
            </>
          )}
        </Button>
      </div>
      {files.length > 0 ? (
        <div className="mt-s3 space-y-2">
          {files.map((file, index) => (
            <div
              key={`${file.name}-${index}`}
              className="flex items-center justify-between rounded-md border border-ds-line bg-ds-sunk px-3 py-2"
            >
              <div className="flex min-w-0 items-center gap-2">
                <File className="h-4 w-4 flex-shrink-0 text-ds-muted" />
                <span className="truncate text-sm text-ds-text">{file.name}</span>
              </div>
              <Button
                type="button"
                variant="danger"
                size="icon"
                className="ml-2 shrink-0"
                onClick={() => setFiles((prev) => prev.filter((_, i) => i !== index))}
                disabled={sending}
                aria-label={t('common.delete')}
              >
                <Trash2 />
              </Button>
            </div>
          ))}
        </div>
      ) : null}
    </form>
  );
}

function ThreadListSkeleton() {
  return (
    <ul className="flex flex-col gap-s1">
      {Array.from({ length: 5 }, (_, i) => (
        <li key={i} className="flex items-start gap-s3 p-s3">
          <Skeleton className="h-10 w-10 shrink-0 rounded-full" />
          <div className="min-w-0 flex-1">
            <Skeleton className="h-4 w-1/2" />
            <SkeletonText lines={2} className="mt-s2" />
          </div>
        </li>
      ))}
    </ul>
  );
}
